import React from 'react';
import { FormikHandlers } from 'formik';

import LabelledInput from '~components/atoms/LabelledInput';
import Text from '~components/atoms/Text';

import { TCreateNewRoleReduxParams } from '~types/feat/admin';
import { ModulePermissions, invalidModules } from '~utils/constants';
import { ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_rolesRolePermissions.module.scss';

const RolesRolePermissions = ({
    accessList,
    handleChange,
    isDisabled,
    allBranchAccess,
}: {
    accessList: TCreateNewRoleReduxParams['access'];
    handleChange: FormikHandlers['handleChange'];
    isDisabled?: boolean;
    allBranchAccess?: boolean;
}
) => {
    const modules = Object.values(ModuleEnum).filter(module => Number.isNaN(Number(module))) as string[];

    const handleInputChange = (type: PermissionsEnum, module: string) => {
        let newAccess: TCreateNewRoleReduxParams['access'] = [];
        const moduleEnum = ModuleEnum[module as keyof typeof ModuleEnum];

        if (accessList.find(access => access.permission === type)) {
            newAccess = accessList.map(access => {
                if (access.permission === type) {
                    const foundPermissionIndex = access.modules.findIndex(item => item === moduleEnum);

                    if (foundPermissionIndex >= 0) {
                        // removing permission
                        return {
                            permission: access.permission,
                            modules: [
                                ...access.modules.slice(0, foundPermissionIndex),
                                ...access.modules.slice(foundPermissionIndex + 1),
                            ],
                        };
                    }

                    // Adding permission
                    return {
                        permission: access.permission,
                        modules: [
                            ...access.modules || [],
                            moduleEnum,
                        ],
                    };
                }

                return access;
            });
        } else {
            newAccess = [
                ...accessList,
                {
                    permission: type,
                    modules: [moduleEnum],
                },
            ];
        }

        handleChange({ target: { name: 'access', value: newAccess } });
    };

    return (
        <div className={Styles['permission-container']}>
            <Text
                label='Page Privileges'
                isBold
            />

            {modules
                // Temporary filter for new margin calculation changes (March 2025), remove the filter below after new changes for admin settings are done
                .filter(module => !invalidModules.includes(ModuleEnum[module as keyof typeof ModuleEnum]))
                .map(module => {
                    const moduleEnum = ModuleEnum[module as keyof typeof ModuleEnum];
                    const isMarketStatusModule = moduleEnum === ModuleEnum.MarketStatus;

                    // Market Status is gated by "All Branches Access":
                    // - while allBranchAccess is OFF: locked unticked, disabled, user cannot touch it
                    // - once allBranchAccess is ON: unlocked, behaves like a normal checkbox (user can freely toggle View/Edit)
                    //   The actual ticked state once unlocked is tracked normally via accessList,
                    //   so the parent form is responsible for pre-ticking it into accessList when allBranchAccess turns on.
                    const marketStatusDisabled = !allBranchAccess;

                    const modulePermissions = ModulePermissions[moduleEnum];
                    const shouldRenderViewPermission = modulePermissions?.includes(PermissionsEnum.View);
                    const shouldRenderCreatePermission = modulePermissions?.includes(PermissionsEnum.Create);
                    const shouldRenderEditPermission = modulePermissions?.includes(PermissionsEnum.Edit);
                    const shouldRenderDeletePermission = modulePermissions?.includes(PermissionsEnum.Delete);

                    const viewInputChecked = accessList.find(access => access.permission === PermissionsEnum.View)?.modules?.includes(moduleEnum) || false;
                    const createInputChecked = accessList.find(access => access.permission === PermissionsEnum.Create)?.modules?.includes(moduleEnum) || false;
                    const editInputChecked = accessList.find(access => access.permission === PermissionsEnum.Edit)?.modules?.includes(moduleEnum) || false;
                    const deleteInputChecked = accessList.find(access => access.permission === PermissionsEnum.Delete)?.modules?.includes(moduleEnum) || false;

                    const inputDisabled = isMarketStatusModule ? (isDisabled || marketStatusDisabled) : isDisabled;

                    if (moduleEnum === ModuleEnum.Dashboard){
                        return (
                            <div
                                key={module}
                                className={Styles['permission-input-container']}
                            >
                                <Text
                                    label={'Dashboard / GoldSync'}
                                    className={Styles['permission-label']}
                                    isBold
                                />

                                <div className={Styles['permission-input-subcontainer']}>
                                    {shouldRenderViewPermission
                                    && (
                                        <LabelledInput
                                            label='View'
                                            onChange={() => handleInputChange(PermissionsEnum.View, module)}
                                            isRow
                                            type='checkbox'
                                            checked={viewInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    )
                                    }

                                    {shouldRenderCreatePermission
                                    && (
                                        <LabelledInput
                                            label='Create'
                                            onChange={() => handleInputChange(PermissionsEnum.Create, module)}
                                            isRow
                                            type='checkbox'
                                            checked={createInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    )
                                    }

                                    {shouldRenderEditPermission
                                    && (
                                        <LabelledInput
                                            label='Edit'
                                            onChange={() => handleInputChange(PermissionsEnum.Edit, module)}
                                            isRow
                                            type='checkbox'
                                            checked={editInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    )
                                    }

                                    {shouldRenderDeletePermission
                                    && (
                                        <LabelledInput
                                            label='Delete'
                                            onChange={() => handleInputChange(PermissionsEnum.Delete, module)}
                                            isRow
                                            type='checkbox'
                                            checked={deleteInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    )
                                    }
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={module}
                            className={Styles['permission-input-container']}
                        >
                            {
                                moduleEnum === ModuleEnum.BuySellGold ?
                                    (
                                        <Text
                                            label={'Buy & Sell Gold'}
                                            className={Styles['permission-label']}
                                            isBold
                                        />
                                    )
                                    :
                                    (
                                        <Text
                                            label={module === module.toUpperCase() ? module : module.replace(/([a-z])([A-Z])/g, '$1 $2')}
                                            className={Styles['permission-label']}
                                            isBold
                                        />
                                    )
                            }

                            <div className={Styles['permission-input-subcontainer']}>
                                {shouldRenderViewPermission
                                && (
                                    <div title={isMarketStatusModule && inputDisabled ? 'Non-admins do not have access to market status permissions' : ''}>
                                        <LabelledInput
                                            label='View'
                                            onChange={() => handleInputChange(PermissionsEnum.View, module)}
                                            isRow
                                            type='checkbox'
                                            checked={viewInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    </div>
                                )
                                }

                                {shouldRenderCreatePermission
                                && (
                                    <LabelledInput
                                        label='Create'
                                        onChange={() => handleInputChange(PermissionsEnum.Create, module)}
                                        isRow
                                        type='checkbox'
                                        checked={createInputChecked}
                                        disabled={inputDisabled}
                                        className={Styles.input}
                                    />
                                )
                                }

                                {shouldRenderEditPermission
                                && (
                                    <div title={isMarketStatusModule && inputDisabled ? 'Non-admins do not have access to market status permissions' : ''}>
                                        <LabelledInput
                                            label='Edit'
                                            onChange={() => handleInputChange(PermissionsEnum.Edit, module)}
                                            isRow
                                            type='checkbox'
                                            checked={editInputChecked}
                                            disabled={inputDisabled}
                                            className={Styles.input}
                                        />
                                    </div>
                                )
                                }

                                {shouldRenderDeletePermission
                                && (
                                    <LabelledInput
                                        label='Delete'
                                        onChange={() => handleInputChange(PermissionsEnum.Delete, module)}
                                        isRow
                                        type='checkbox'
                                        checked={deleteInputChecked}
                                        disabled={inputDisabled}
                                        className={Styles.input}
                                    />
                                )
                                }
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default RolesRolePermissions;
